import { useWorkspaceId } from "../../hooks/useWorkspaceId";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHttpTaxManagerAppHeaders } from "../utils/useHttpTaxManagerAppHeaders";
import { BASE_API_URL, HttpError } from "../utils/useTaxManagerAppQueryClient";

export interface TaxTinderSuggestionApplication {
    levyId: string;
    year: number;
    taxPayerWorkspaceId: string;
}

interface ApplyTaxTinderSuggestionsRequest {
    suggestions: TaxTinderSuggestionApplication[];
}

export type TaxTinderFailureReason = "ALREADY_ASSOCIATED" | "UNKNOWN";

export interface FailedTaxTinderSuggestion extends TaxTinderSuggestionApplication {
    reason?: string;
}

export interface ApplyTaxTinderSuggestionsResult {
    failed: FailedTaxTinderSuggestion[];
}

export function useApplyTaxTinderSuggestions() {
    const workspaceId = useWorkspaceId();
    const headers = useHttpTaxManagerAppHeaders({ method: "POST" });
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const mutation = useMutation<
        ApplyTaxTinderSuggestionsResult,
        Error,
        ApplyTaxTinderSuggestionsRequest
    >({
        mutationFn: async (request) => {
            const response = await fetch(
                `${BASE_API_URL}/tenants/${workspaceId}/fulfilments/tax-tinder`,
                {
                    headers,
                    method: "POST",
                    body: JSON.stringify({
                        suggestions: request.suggestions,
                    }),
                },
            );

            if (!response.ok) {
                throw new HttpError(response);
            }

            if (response.status === 207) {
                const body = (await response.json()) as {
                    failed?: FailedTaxTinderSuggestion[];
                };
                return { failed: body.failed ?? [] };
            }

            return { failed: [] };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["useGetFulfilments"] });
            queryClient.invalidateQueries({
                queryKey: ["useGetLinkableFulfilments"],
            });
            queryClient.invalidateQueries({
                queryKey: ["useGetLinkableTaxPayers"],
            });
            queryClient.invalidateQueries({
                queryKey: ["useGetFulfilmentTaxPayers"],
            });
            queryClient.invalidateQueries({
                queryKey: ["useGetTaxTinderSuggestions"],
            });
            queryClient.invalidateQueries({ queryKey: ["useGetKPIs"] });
        },
        meta: {
            errorMessage: t("api.apply_tax_tinder_suggestions.failed"),
        },
    });

    return mutation;
}
